import { createContext, useContext, useState, useEffect } from "react";
import api, { STAFF_TOKEN_KEY, STAFF_SESSION_EXPIRED } from "@/lib/api";
import { watchExpiry } from "@/lib/session";
import { clearOpened } from "@/lib/openedDocs";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(STAFF_TOKEN_KEY);
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => {
        localStorage.removeItem(STAFF_TOKEN_KEY);
        setUser(false);
      })
      .finally(() => setLoading(false));
  }, []);

  // session timed out (see lib/session.js): back to the login page
  useEffect(() => {
    const expired = () => { clearOpened(); setUser(false); };
    window.addEventListener(STAFF_SESSION_EXPIRED, expired);
    const stopWatching = watchExpiry(STAFF_TOKEN_KEY, expired);
    return () => { window.removeEventListener(STAFF_SESSION_EXPIRED, expired); stopWatching(); };
  }, []);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem(STAFF_TOKEN_KEY, res.data.token);
    clearOpened();  // a new session starts with nothing opened
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    localStorage.removeItem(STAFF_TOKEN_KEY);
    clearOpened();
    setUser(false);
  };

  const refreshUser = async () => {
    const res = await api.get("/auth/me");
    setUser(res.data);
    return res.data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
